import styled, { StyledComponentPropsWithRef } from 'styled-components'

import { theme } from '../../styles/theme'
import type { SvgIcons } from '../Icon/Icon'
import { Label } from '../Typography/Typography'

import { selectSvgIconClassName } from '../../styles/globalCss'
import { TabGroup } from '../TabGroup/TabGroup'
import { Column } from '../Table/Table'

const SelectOuter = styled.div<WithIsDisabled>`
  text-align: start;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'initial')};
`

interface SelectLabelProps extends WithIsValid, WithIsDisabled {
  isMultiple: boolean
}

const StyledLabel = styled(Label)`
  transition: all 0.3s ease-in;
  display: flex;
  flex-direction: column;
  padding: 0em 0.5em 0em 0.5em;
  height: 0px;
  width: 100%;
  opacity: 0;
  font-size: ${theme.fontSizes.small};
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.SemiDarkGrey};
  font-weight: ${theme.fontWeights.bold};

  :not([value='']){
    color: ${theme.colors.SemiDarkGrey};
    transition: all ${theme.transitions.normal} ease-in-out;
    height: 11px;
    opacity: 1;
    font-size: ${theme.fontSizes.small};
    margin: 0.2em 0em 0.2em 0em;
  }
`

const SelectBox = styled(Label)<SelectLabelProps>`
  // note that the SelectBox and InputBox are aligned in terms of default padding
  padding: 0em 0em 0em 0.5em;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5em;
  height: 4em;

  flex-grow: 1;

  position: relative;
  text-transform: none;

  background-color: ${theme.colors.White};

  color: ${theme.colors.MainBlack};  # font color

  grid-gap: 1em;
  
  /* the background-image is the dropdown arrow */
  background-image:
    linear-gradient(45deg, transparent 50%, gray 50%), /* left side triangle */
    linear-gradient(135deg, gray 50%, transparent 50%); /* right side triangle */
  background-position:
    calc(100% - 1em) calc(50%),
    calc(100% - 1em + 5px) calc(50%);
  background-size:
    5px 5px,
    5px 5px;
  background-repeat: no-repeat;
  background-origin: padding-box; /* so the background-image can fit over the padding */

  transition: all ${theme.transitions.normal} ease-out;
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'all')};
  
  border-radius: ${theme.radii.small};
  
  ${TabGroup} &[class] {
    // inside TabGroup we have no borders
    border: none;  
  }
  border: ${theme.borders.menu};
  
  :hover {
    border-color: ${theme.colors.SemiDarkGrey};
  }
`

interface WithOptions {
  options: SelectOptions
  isNothingSelected?: boolean
}

interface StyledSelectProps extends WithOptions, MaybeWithIsValid {}

const StyledSelect = styled.select<StyledSelectProps>`
  // note that the StyledSelect and StyledInput are aligned in terms of default padding
  padding: 0.5em;
  inline-size: 100%;
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.bold};
  min-width: 3em;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;

  // We have one font color for the place holder tag and other color for the option when it has been selected:
  color: ${({ isNothingSelected = true }) => (isNothingSelected ? theme.colors.SemiDarkGrey : theme.colors.Black)};

  ::-webkit-scrollbar-thumb {
    /* Transitions don't work on scrollbars :( */
    /* stylelint-disable-next-line property-disallowed-list -- Has to be background and not background-color */
    background: ${({ isValid = true }) => (isValid ? 'inherit' : theme.colors.danger)};
  }
  :hover::-webkit-scrollbar-thumb {
    /* stylelint-disable-next-line property-disallowed-list -- Has to be background and not background-color */
    background: ${theme.colors.GreenDark};
  }
`

const Option = styled.option`
  margin: 0.5ch;
  padding: 0.5ch;
  :first-child {
    margin-block-start: 0;
  }
  :last-child {
    margin-block-end: 0;
  }
`

const ValidationMessageList = styled.ul`
  color: ${theme.colors.danger};
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  :not(:empty) {
    padding: 0.8em;
    padding-inline-start: 0;
  }
`

const ValidationMessage = styled.li``


export interface SelectOption extends WithText {
  value: string
}

export const selectOptionFromString = (s: string): SelectOption => ({ text: s, value: s})

export type SelectOptions = ReadonlyArray<SelectOption>

const ValueText = styled.span`
  color: ${theme.colors.MainBlack};
  font-weight:${theme.fontWeights.regular};
  padding: 0.8em 0;
`

export interface SelectProps
  extends MaybeWithIsDisabled,
    MaybeWithLabel,
    MaybeWithTitle,
    MaybeWithIsRequired,
    MaybeWithValidationMessages,
    MaybeWithIsValid,
    StyledSelectProps,
    StyledComponentPropsWithRef<typeof StyledSelect> {
  selectedOptions: SelectOptions
  isMultiple?: boolean
  name: string
  selectStyle?: 'regular' | 'view-only'
  StartIcon?: typeof SvgIcons.ArrowSvg
}

function SelectComponent({
  label,
  options,
  selectedOptions,
  title = label,
  showLabel = true,
  isDisabled = false,
  validationMessages = [],
  style = {},
  className = '',
  isMultiple = false,
  isRequired = true,
  isValid = validationMessages.length === 0,
  selectStyle = 'regular',
  StartIcon,
  isNothingSelected = false,
  ...props
}: SelectProps) {
  // https://stackoverflow.com/a/53064469/915875
  const value = isMultiple ? selectedOptions.map((option) => option.value) : selectedOptions[0]?.value ?? ''
  const selectedOptionText = selectedOptions.map((option) => option.text).join(",")
  isNothingSelected = value === ''
  return (
    <SelectOuter {...{ title, style, className, isDisabled }}>
      { selectStyle === 'view-only' ? 
        <ValueText> {selectedOptions.map(option => option.text).join() } </ValueText> : 
        <SelectBox {...{ isDisabled, isValid, isMultiple, value }}>
          {StartIcon && <StartIcon className={selectSvgIconClassName} />}
          <Column {...{style:{gap: '0', width: '100%'}}}>
            {
              showLabel && <StyledLabel {...{value: value}}>{label}</StyledLabel>
            }
            <StyledSelect
              {...{
                options,
                value,
                title: selectedOptionText || title,
                multiple: isMultiple,
                disabled: isDisabled,
                required: isRequired,
                isValid,
                isNothingSelected,
                ...props,
              }}
            >
              {isNothingSelected ? (
                <Option {...{ value: `${label}`, children: `${label}`, key: 'select-an-option', disabled: false, hidden: true }} />
              ) : null}
              {options.map(({ value, text }) => (
                <Option {...{ key: `${text}__${value}`, value, children: text }} />
              ))}
            </StyledSelect>
          </Column>
        </SelectBox>
      }
      <ValidationMessageList>
        {validationMessages.map((message) => (
          <ValidationMessage {...{ key: message, children: message }} />
        ))}
      </ValidationMessageList>
    </SelectOuter>
  )
}

export const Select = styled(SelectComponent)`
  /* https://stackoverflow.com/a/66689926/915875 */
  min-inline-size: 0;
  text-transform: none;
  color: ${theme.colors.body};
  font-weight: ${theme.fontWeights.regular};
`

export type SelectType = typeof SelectComponent
